import { useState } from 'react';

import { useParams } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Chip, TableRow, Collapse, TableCell, IconButton, Link } from '@mui/material';

import { MESSAGES } from '../../../../constants/messages';
import { paths } from '../../../../routes/paths';
import { formatDate } from '../../shared/utils/policyUtils';
import { TABLE_HEAD } from './transaction-history.constants';
import { type ExpandableRowProps } from './transaction-history.types';
import { getStatusChipSx, hasDetailSections } from './transaction-history.utils';
import { TransactionHistoryDetail } from './components/transaction-history-detail';

export function TransactionHistoryRow({ row, index }: Readonly<ExpandableRowProps>) {
  const [open, setOpen] = useState(false);
  const showDetails = hasDetailSections(row);
  const { comboId, coverageId, policyId } = useParams<{ comboId: string; coverageId: string; policyId: string }>();

  // Extract transaction type cell content for Sonar clarity
  let transactionTypeCellContent: React.ReactNode = '';
  if (row.transactionTypeDescription) {
    if (row.reportNumber != null && row.correctionSequenceNumber != null && row.transactionStatus !== MESSAGES.TRANSACTION_HISTORY.TRANSACTION_STATUS.REJECTED && comboId && coverageId && policyId) {
      const usrPath = paths.dashboard.employer_data.usr_details(
        comboId,
        coverageId,
        policyId,
        row.reportNumber,
        row.correctionSequenceNumber
      );
      transactionTypeCellContent = (
        <Link
          component={RouterLink}
          to={usrPath}
          underline="hover"
          sx={{ cursor: 'pointer' }}
          data-testid={`transaction-history-transaction-type-link-${index}`}
        >
          {row.transactionTypeDescription}
        </Link>
      );
    } else if (row.transactionStatus === MESSAGES.TRANSACTION_HISTORY.TRANSACTION_STATUS.REJECTED) {
      transactionTypeCellContent = (
        <Link
          href={`/data-management/rejected-transactions?submissionId=${row.submissionId}`}
          underline="hover"
          sx={{ cursor: 'pointer' }}
          data-testid={`transaction-history-transaction-type-link-${index}`}
        >
          {row.transactionTypeDescription}
        </Link>
      );
    } else {
      transactionTypeCellContent = row.transactionTypeDescription;
    }
  }

  return (
    <>
      <TableRow hover tabIndex={-1} data-testid={`transaction-history-row-${index}`}>
        <TableCell sx={{ width: 40, pr: 0 }}>
          {showDetails && (
            <IconButton
              size="small"
              onClick={() => setOpen((prev) => !prev)}
              aria-expanded={open}
              aria-label="expand row"
              data-testid={`transaction-history-expand-${index}`}
            >
              <ExpandMoreIcon
                sx={{
                  transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              />
            </IconButton>
          )}
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }} data-testid={`transaction-history-submission-id-${index}`}>
          {row.submissionId}
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }} data-testid={`transaction-history-transaction-type-${index}`}>
          {transactionTypeCellContent}
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }} data-testid={`transaction-history-received-date-${index}`}>
          {formatDate(row.receivedDate ? new Date(row.receivedDate) : undefined)}
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }} data-testid={`transaction-history-issue-date-${index}`}>
          {row.issueDate ? formatDate(new Date(row.issueDate)) : ''}
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }} data-testid={`transaction-history-status-${index}`}>
          {row.transactionStatus ? (
            <Chip
              label={row.transactionStatus}
              size="small"
              variant="outlined"
              sx={getStatusChipSx(row.transactionStatus)}
              data-testid={`transaction-history-status-chip-${index}`}
            />
          ) : null}
        </TableCell>
      </TableRow>

      {showDetails && (
        <TableRow data-testid={`transaction-history-detail-row-${index}`}>
          <TableCell
            colSpan={TABLE_HEAD.length}
            sx={(theme) => ({
              paddingBottom: 0,
              paddingTop: 0,
              backgroundColor: open
                ? `rgba(${theme.vars.palette.grey['500Channel']} / 0.4)`
                : 'inherit',
            })}
          >
            <Collapse in={open} timeout="auto" >
              <Box sx={{ paddingBottom: '1rem', paddingTop: '1rem' }}>
                <TransactionHistoryDetail row={row} index={index} />
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}